import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Multiselect,
  Textarea,
  Select,
  StatusIndicator,
  Flashbar,
  Box,
  Badge,
  RadioGroup,
  Link,
  FormField,
  ExpandableSection,
  Input,
  Modal,
} from '@cloudscape-design/components';
import {
  submitFeedback,
  updateFeedback,
  deleteFeedback,
  getFeedback,
  submitReply,
  updateReply,
  deleteReply,
  resolveFeedback,
} from '../services/s3Service';
import { getProgramFeedbackConfig } from '../programs/registry';
import {
  REPLY_DISPOSITIONS,
  getFeedbackStatus,
  getReplyDisposition,
  getReplyDispositionBadgeColor,
  getReplyDispositionDescription,
  getReplyDispositionLabel,
  getSimTicketUrlError,
  isActionableReplyDisposition,
} from '../utils/feedbackUtils';

const ReviewerFeedbackPanel = ({ fundRequestId, program, subProgram, validationDecision, onBlindReviewPending }) => {
  const programConfig = getProgramFeedbackConfig(program, subProgram);
  const decisionOptions = programConfig.decisionOptions;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [selectedValidations, setSelectedValidations] = useState([]);
  const [disagreementReason, setDisagreementReason] = useState('');
  const [recommendedDecision, setRecommendedDecision] = useState(null);
  const [reviewerNote, setReviewerNote] = useState('');
  const [flashItems, setFlashItems] = useState([]);
  const [replyDisposition, setReplyDisposition] = useState(null);
  const [replyExplanation, setReplyExplanation] = useState('');
  const [simTicketUrl, setSimTicketUrl] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [canReplyToFeedback, setCanReplyToFeedback] = useState(false);
  const [allFeedback, setAllFeedback] = useState([]);
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [editingReply, setEditingReply] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [resolvingIds, setResolvingIds] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadExistingFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFeedback(fundRequestId);
      setExistingFeedback(data.feedback || null);
      setCanReplyToFeedback(data.canReplyToFeedback || false);
      setAllFeedback(data.allFeedback || []);
      return true;
    } catch (err) {
      console.error('Failed to load feedback:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fundRequestId]);

  useEffect(() => {
    loadExistingFeedback();
  }, [loadExistingFeedback]);

  const validationOptions = programConfig.validations.map((validation) => ({
    label: validation.label,
    value: validation.key,
    description: validation.description,
  }));

  const labelForStoredKey = (storedKey) => {
    const match = programConfig.validations.find((validation) => validation.key === storedKey);
    return match ? match.label : storedKey;
  };

  const showFlash = (type, content, id, action) => {
    setFlashItems([
      {
        type,
        content,
        dismissible: true,
        onDismiss: () => setFlashItems([]),
        id,
        action,
      },
    ]);
  };

  // Used when a change is saved but the panel still shows the previous state.
  const showSavedWithStaleView = (content) => {
    showFlash(
      'warning',
      `${content} The view could not be refreshed, so reload the page to see the current state.`,
      'refresh-warning'
    );
  };

  const isDisagreeValid =
    feedbackType === 'DISAGREE' &&
    selectedValidations.length > 0 &&
    disagreementReason.trim().length > 0 &&
    recommendedDecision !== null;

  const canSubmit = feedbackType === 'AGREE' || isDisagreeValid;

  const startEditing = () => {
    if (existingFeedback) {
      setFeedbackType(existingFeedback.feedbackType);
      setSelectedValidations(
        (existingFeedback.disagreementValidations || []).map((validation) => ({
          label: labelForStoredKey(validation),
          value: validation,
        }))
      );
      setDisagreementReason(existingFeedback.disagreementReason || '');
      setReviewerNote(existingFeedback.reviewerNote || '');
      setRecommendedDecision(
        existingFeedback.recommendedDecision
          ? decisionOptions.find((option) => option.value === existingFeedback.recommendedDecision) || null
          : null
      );
    }
    setIsEditing(true);
    setFlashItems([]);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setFeedbackType(null);
    setSelectedValidations([]);
    setDisagreementReason('');
    setRecommendedDecision(null);
    setReviewerNote('');
    setFlashItems([]);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setFlashItems([]);

      const disagreeValidations = feedbackType === 'DISAGREE'
        ? selectedValidations.map((selection) => selection.value)
        : null;
      const disagreeReason = feedbackType === 'DISAGREE' ? disagreementReason : null;
      const recDecision = feedbackType === 'DISAGREE' && recommendedDecision
        ? recommendedDecision.value
        : null;
      const note = feedbackType === 'AGREE' ? reviewerNote.trim() || null : null;

      if (isEditing) {
        await updateFeedback(
          fundRequestId,
          feedbackType,
          validationDecision,
          disagreeValidations,
          disagreeReason,
          recDecision,
          note
        );
      } else {
        await submitFeedback(
          fundRequestId,
          programConfig.programId,
          feedbackType,
          validationDecision,
          disagreeValidations,
          disagreeReason,
          recDecision,
          note
        );
      }

      setIsEditing(false);
      const message = isEditing
        ? 'Feedback updated successfully.'
        : 'Feedback submitted successfully.';
      if (await loadExistingFeedback()) {
        showFlash('success', message, 'feedback-success');
      } else {
        showSavedWithStaleView(message);
      }
    } catch (err) {
      if (err.status === 409 && /^Blind review pending/.test(err.message || '')) {
        // A re-validation made this fund request blind: the page must reload to
        // show the blind form, so the flash offers that reload.
        showFlash(
          'info',
          err.message,
          'feedback-blind-review',
          onBlindReviewPending ? <Button onClick={onBlindReviewPending}>Reload</Button> : undefined
        );
      } else if (err.status === 409) {
        showFlash('info', 'Feedback was already submitted for this fund request.', 'feedback-conflict');
        await loadExistingFeedback();
      } else {
        showFlash(
          'error',
          `Failed to ${isEditing ? 'update' : 'submit'} feedback: ${err.message}`,
          'feedback-error',
          <Button onClick={handleSubmit}>Retry</Button>
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDecision = (value) => {
    const option = decisionOptions.find((decision) => decision.value === value);
    return option ? option.label : value;
  };

  const resetReplyForm = () => {
    setReplyDisposition(null);
    setReplyExplanation('');
    setSimTicketUrl('');
    setReplyTargetId(null);
    setEditingReply(false);
  };

  const startReply = (feedback, edit = false) => {
    const storedDisposition = edit ? feedback.replyDisposition : null;
    setReplyDisposition(getReplyDisposition(storedDisposition) ? storedDisposition : null);
    setReplyExplanation(edit ? feedback.replyExplanation || '' : '');
    setSimTicketUrl(edit ? feedback.simTicketUrl || '' : '');
    setReplyTargetId(feedback.reviewerId);
    setEditingReply(edit);
    setFlashItems([]);
  };

  const simTicketUrlError = getSimTicketUrlError(simTicketUrl);
  const isReplyValid =
    replyDisposition !== null &&
    replyExplanation.trim().length > 0 &&
    simTicketUrlError === null;

  const handleReplySubmit = async (targetFeedback) => {
    if (!isReplyValid || !targetFeedback) return;

    try {
      setSubmittingReply(true);
      setFlashItems([]);
      const saveReply = editingReply ? updateReply : submitReply;
      await saveReply(
        targetFeedback.fundRequestId,
        targetFeedback.reviewerId,
        replyDisposition,
        replyExplanation.trim(),
        simTicketUrl.trim() || null
      );

      const message = editingReply
        ? 'Reply updated successfully.'
        : 'Reply submitted successfully.';
      resetReplyForm();
      if (await loadExistingFeedback()) {
        showFlash('success', message, 'reply-success');
      } else {
        showSavedWithStaleView(message);
      }
    } catch (err) {
      if (!editingReply && err.status === 409) {
        showFlash('info', 'A reply has already been submitted for this feedback.', 'reply-conflict');
        await loadExistingFeedback();
      } else {
        showFlash(
          'error',
          `Failed to ${editingReply ? 'update' : 'submit'} reply: ${err.message}`,
          'reply-error',
          <Button onClick={() => handleReplySubmit(targetFeedback)}>Retry</Button>
        );
      }
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleResolve = async (targetFeedback) => {
    if (!targetFeedback || !targetFeedback.reviewerId) return;
    const itemKey = `${fundRequestId}:${targetFeedback.reviewerId}`;
    setResolvingIds((previous) => new Set(previous).add(itemKey));
    setFlashItems([]);
    try {
      await resolveFeedback(fundRequestId, targetFeedback.reviewerId);
      if (await loadExistingFeedback()) {
        showFlash('success', 'Feedback marked as resolved.', 'resolve-success');
      } else {
        showSavedWithStaleView('Feedback marked as resolved.');
      }
    } catch (err) {
      if (err.status === 409) {
        showFlash(
          'info',
          err.message || 'Feedback is already resolved or not eligible.',
          'resolve-conflict'
        );
        await loadExistingFeedback();
      } else {
        showFlash('error', `Failed to mark resolved: ${err.message}`, 'resolve-error');
      }
    } finally {
      setResolvingIds((previous) => {
        const next = new Set(previous);
        next.delete(itemKey);
        return next;
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { type: deletedType, feedback: targetFeedback } = deleteTarget;

    try {
      setDeleting(true);
      setFlashItems([]);
      if (deletedType === 'reply') {
        await deleteReply(targetFeedback.fundRequestId, targetFeedback.reviewerId);
      } else {
        await deleteFeedback(fundRequestId);
      }
      setDeleteTarget(null);
      resetReplyForm();
      const message =
        deletedType === 'reply' ? 'Reply deleted successfully.' : 'Feedback deleted successfully.';
      if (await loadExistingFeedback()) {
        showFlash('success', message, 'delete-success');
      } else {
        showSavedWithStaleView(message);
      }
    } catch (err) {
      setDeleteTarget(null);
      showFlash('error', `Failed to delete ${deletedType}: ${err.message}`, 'delete-error');
    } finally {
      setDeleting(false);
    }
  };

  const buildSummary = () => {
    if (loading) return 'Loading...';
    if (!existingFeedback) {
      if (allFeedback.length > 0) {
        return `No feedback from you · ${allFeedback.length} other reviewer(s)`;
      }
      return 'No feedback submitted';
    }
    const icon = existingFeedback.feedbackType === 'AGREE' ? '✅' : '⚠️';
    const date = existingFeedback.submittedAt
      ? new Date(existingFeedback.submittedAt).toLocaleDateString()
      : '';
    let summary = `${icon} You: ${existingFeedback.feedbackType}`;
    if (existingFeedback.feedbackType === 'DISAGREE' && existingFeedback.recommendedDecision) {
      const recommendation = decisionOptions.find(
        (option) => option.value === existingFeedback.recommendedDecision
      );
      summary += ` → ${recommendation ? recommendation.label : existingFeedback.recommendedDecision}`;
    }
    if (getFeedbackStatus(existingFeedback) === 'Resolved') {
      summary += ' · ✅ Resolved';
    }
    if (date) summary += ` · ${date}`;
    const otherCount = allFeedback.filter(
      (feedback) => feedback.reviewerId !== existingFeedback.reviewerId
    ).length;
    if (otherCount > 0) summary += ` · +${otherCount} other reviewer(s)`;
    return summary;
  };

  const deleteConfirmationModal = (
    <Modal
      visible={deleteTarget !== null}
      onDismiss={() => !deleting && setDeleteTarget(null)}
      header={deleteTarget?.type === 'reply' ? 'Delete reply?' : 'Delete feedback?'}
      closeAriaLabel="Close delete confirmation"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {deleteTarget?.type === 'reply'
        ? 'This reply will be permanently deleted. The team can add a new reply afterward.'
        : 'Your original feedback, its reply, linked SIM-T, and resolution history will be permanently deleted.'}
    </Modal>
  );

  if (loading) {
    return (
      <ExpandableSection
        variant="container"
        headerText="Reviewer Feedback"
        headerDescription={buildSummary()}
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
      >
        <StatusIndicator type="loading">Loading feedback...</StatusIndicator>
      </ExpandableSection>
    );
  }

  const renderFeedbackCard = (feedback, isOwnFeedback) => {
    const isResolved = getFeedbackStatus(feedback) === 'Resolved';
    const itemKey = `${fundRequestId}:${feedback.reviewerId}`;
    const isResolvingThis = resolvingIds.has(itemKey);
    const isReplyFormOpen = replyTargetId === feedback.reviewerId;
    const canManageReply = canReplyToFeedback;
    const showReplyForm =
      feedback.feedbackType === 'DISAGREE' &&
      canReplyToFeedback &&
      isReplyFormOpen &&
      (feedback.replyDisposition == null || editingReply);

    return (
      <Container
        key={feedback.reviewerId}
        header={
          <Header
            variant="h3"
            actions={isOwnFeedback ? (
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={startEditing}>Edit</Button>
                <Button onClick={() => setDeleteTarget({ type: 'feedback', feedback })}>Delete</Button>
              </SpaceBetween>
            ) : null}
          >
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <span>
                {isOwnFeedback
                  ? 'Your Feedback'
                  : `Feedback by ${feedback.reviewerAlias || feedback.reviewerId}`}
              </span>
              {isResolved && <Badge color="green">Resolved</Badge>}
            </SpaceBetween>
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Box>
            <Box variant="awsui-key-label">Feedback</Box>
            <StatusIndicator type={feedback.feedbackType === 'AGREE' ? 'success' : 'warning'}>
              {feedback.feedbackType}
            </StatusIndicator>
          </Box>
          {feedback.recommendedDecision && (
            <Box>
              <Box variant="awsui-key-label">Recommended decision</Box>
              <Box>{formatDecision(feedback.recommendedDecision)}</Box>
            </Box>
          )}
          {feedback.disagreementValidations && feedback.disagreementValidations.length > 0 && (
            <Box>
              <Box variant="awsui-key-label">Disagreed validations</Box>
              <SpaceBetween direction="horizontal" size="xs">
                {feedback.disagreementValidations.map((validation) => (
                  <Badge key={validation} color="red">{labelForStoredKey(validation)}</Badge>
                ))}
              </SpaceBetween>
            </Box>
          )}
          {feedback.disagreementReason && (
            <Box>
              <Box variant="awsui-key-label">Reason</Box>
              <Box>{feedback.disagreementReason}</Box>
            </Box>
          )}
          {feedback.reviewerNote && (
            <Box>
              <Box variant="awsui-key-label">Reviewer note</Box>
              <Box>{feedback.reviewerNote}</Box>
            </Box>
          )}
          {feedback.submittedAt && (
            <Box>
              <Box variant="awsui-key-label">Submitted</Box>
              <Box>{new Date(feedback.submittedAt).toLocaleString()}</Box>
            </Box>
          )}

          {feedback.replyDisposition != null && !(isReplyFormOpen && editingReply) && (
            <Container
              header={
                <Header
                  variant="h3"
                  actions={canManageReply ? (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button onClick={() => startReply(feedback, true)}>Edit</Button>
                      <Button onClick={() => setDeleteTarget({ type: 'reply', feedback })}>Delete</Button>
                    </SpaceBetween>
                  ) : null}
                >
                  Reply
                </Header>
              }
            >
              <SpaceBetween size="s">
                <Box>
                  <Box variant="awsui-key-label">Disposition</Box>
                  <Badge color={getReplyDispositionBadgeColor(feedback.replyDisposition)}>
                    {getReplyDispositionLabel(feedback.replyDisposition)}
                  </Badge>
                  {getReplyDispositionDescription(feedback.replyDisposition) && (
                    <Box variant="small" color="text-body-secondary" padding={{ top: 'xxs' }}>
                      {getReplyDispositionDescription(feedback.replyDisposition)}
                    </Box>
                  )}
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Explanation</Box>
                  <Box>{feedback.replyExplanation}</Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Responder</Box>
                  <Box>{feedback.replyResponderAlias || '—'}</Box>
                </Box>
                {feedback.replyTimestamp && (
                  <Box>
                    <Box variant="awsui-key-label">Replied at</Box>
                    <Box>{new Date(feedback.replyTimestamp).toLocaleString()}</Box>
                  </Box>
                )}
                {feedback.replyUpdatedAt && (
                  <Box>
                    <Box variant="awsui-key-label">Last edited</Box>
                    <Box>
                      {new Date(feedback.replyUpdatedAt).toLocaleString()} by{' '}
                      {feedback.replyUpdatedBy || '—'}
                    </Box>
                  </Box>
                )}
                {feedback.simTicketUrl && (
                  <Box>
                    <Box variant="awsui-key-label">SIM-T</Box>
                    <Link
                      href={feedback.simTicketUrl}
                      external
                      variant="primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View SIM-T
                    </Link>
                  </Box>
                )}
                {feedback.resolvedAt && (
                  <>
                    <Box>
                      <Box variant="awsui-key-label">Resolved by</Box>
                      <Box>{feedback.resolvedBy}</Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Resolved at</Box>
                      <Box>{new Date(feedback.resolvedAt).toLocaleString()}</Box>
                    </Box>
                  </>
                )}
                {isActionableReplyDisposition(feedback.replyDisposition) &&
                  !feedback.resolvedAt &&
                  canReplyToFeedback && (
                    <Button
                      onClick={() => handleResolve(feedback)}
                      loading={isResolvingThis}
                      disabled={isResolvingThis}
                    >
                      Mark Resolved
                    </Button>
                  )}
              </SpaceBetween>
            </Container>
          )}

          {showReplyForm && (
            <Container header={<Header variant="h3">{editingReply ? 'Edit Reply' : 'Reply to Feedback'}</Header>}>
              <SpaceBetween size="m">
                <FormField label="Disposition">
                  <RadioGroup
                    value={replyDisposition}
                    onChange={({ detail }) => setReplyDisposition(detail.value)}
                    items={REPLY_DISPOSITIONS}
                  />
                </FormField>
                <FormField
                  label="Explanation"
                  errorText={
                    replyExplanation.length > 0 && replyExplanation.trim().length === 0
                      ? 'Explanation cannot be empty or whitespace only.'
                      : undefined
                  }
                  constraintText="Required — explain your disposition."
                >
                  <Textarea
                    value={replyExplanation}
                    onChange={({ detail }) => setReplyExplanation(detail.value)}
                    placeholder="Provide an explanation for your reply..."
                    rows={3}
                  />
                </FormField>
                <FormField
                  label="SIM-T URL (optional)"
                  description="Link an existing SIM-T ticket when one is available."
                  constraintText="Optional — must be an HTTPS URL on t.corp.amazon.com."
                  errorText={simTicketUrl ? simTicketUrlError : undefined}
                >
                  <Input
                    value={simTicketUrl}
                    onChange={({ detail }) => setSimTicketUrl(detail.value)}
                    placeholder="https://t.corp.amazon.com/..."
                    type="url"
                  />
                </FormField>
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    onClick={() => handleReplySubmit(feedback)}
                    loading={submittingReply}
                    disabled={!isReplyValid || submittingReply}
                  >
                    {editingReply ? 'Update Reply' : 'Submit Reply'}
                  </Button>
                  <Button onClick={resetReplyForm}>Cancel</Button>
                </SpaceBetween>
              </SpaceBetween>
            </Container>
          )}

          {feedback.feedbackType === 'DISAGREE' &&
            feedback.replyDisposition == null &&
            canReplyToFeedback &&
            !isReplyFormOpen && (
              <Button onClick={() => startReply(feedback)}>Reply</Button>
            )}
        </SpaceBetween>
      </Container>
    );
  };

  if (existingFeedback && !isEditing) {
    const otherFeedback = allFeedback.filter(
      (feedback) => feedback.reviewerId !== existingFeedback.reviewerId
    );

    return (
      <ExpandableSection
        variant="container"
        headerText="Reviewer Feedback"
        headerDescription={buildSummary()}
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
      >
        <SpaceBetween size="l">
          <Flashbar items={flashItems} />
          {renderFeedbackCard(existingFeedback, true)}
          {otherFeedback.map((feedback) => renderFeedbackCard(feedback, false))}
        </SpaceBetween>
        {deleteConfirmationModal}
      </ExpandableSection>
    );
  }

  const feedbackToDisplay = existingFeedback
    ? allFeedback.filter((feedback) => feedback.reviewerId !== existingFeedback.reviewerId)
    : allFeedback;

  return (
    <ExpandableSection
      variant="container"
      headerText="Reviewer Feedback"
      headerDescription={buildSummary()}
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
    >
      <Container
        header={
          <Header variant="h3" actions={isEditing ? <Button onClick={cancelEditing}>Cancel</Button> : null}>
            Reviewer Feedback {isEditing ? '(Editing)' : ''}
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Flashbar items={flashItems} />
          <Box>
            <Box variant="awsui-key-label">Do you agree with Chronos's validation decision?</Box>
            <Box padding={{ top: 'xxs' }}>
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant={feedbackType === 'AGREE' ? 'primary' : 'normal'}
                  onClick={() => {
                    setFeedbackType('AGREE');
                    setSelectedValidations([]);
                    setDisagreementReason('');
                    setRecommendedDecision(null);
                  }}
                >
                  Agree
                </Button>
                <Button
                  variant={feedbackType === 'DISAGREE' ? 'primary' : 'normal'}
                  onClick={() => setFeedbackType('DISAGREE')}
                >
                  Disagree
                </Button>
              </SpaceBetween>
            </Box>
          </Box>

          {feedbackType === 'AGREE' && (
            <Textarea
              value={reviewerNote}
              onChange={({ detail }) => setReviewerNote(detail.value)}
              placeholder="Add any notes or observations (optional)"
              rows={3}
            />
          )}

          {feedbackType === 'DISAGREE' && (
            <SpaceBetween size="s">
              <Select
                selectedOption={recommendedDecision}
                onChange={({ detail }) => setRecommendedDecision(detail.selectedOption)}
                options={decisionOptions}
                placeholder="What should the decision be?"
              />
              <Multiselect
                selectedOptions={selectedValidations}
                onChange={({ detail }) => setSelectedValidations(detail.selectedOptions)}
                options={validationOptions}
                placeholder="Select validation checks you disagree with"
                filteringType="auto"
              />
              <Textarea
                value={disagreementReason}
                onChange={({ detail }) => setDisagreementReason(detail.value)}
                placeholder="Explain why you disagree with the validation decision..."
                rows={3}
              />
              {!isDisagreeValid &&
                (selectedValidations.length === 0 ||
                  disagreementReason.trim().length === 0 ||
                  !recommendedDecision) && (
                  <StatusIndicator type="warning">
                    A recommended decision, validation selection, and reason are all required.
                  </StatusIndicator>
                )}
            </SpaceBetween>
          )}

          {feedbackType && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!canSubmit || submitting}
            >
              {isEditing ? 'Update Feedback' : 'Submit Feedback'}
            </Button>
          )}
        </SpaceBetween>
      </Container>
      {feedbackToDisplay.length > 0 && (
        <Box padding={{ top: 'm' }}>
          <SpaceBetween size="l">
            {feedbackToDisplay.map((feedback) => renderFeedbackCard(feedback, false))}
          </SpaceBetween>
        </Box>
      )}
      {deleteConfirmationModal}
    </ExpandableSection>
  );
};

export default ReviewerFeedbackPanel;
